import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { FILAS_DEFS, FILA_INFO, FilaItem } from '@/pages/painel/lib/inteligencia';
import FilaCard from './FilaCard';

interface FilasData {
  filas: Record<string, FilaItem[]>;
  resumo: Record<string, number>;
  total: number;
}

export default function FilasModule() {
  const { password } = useOutletContext<{ password: string }>();
  const [data, setData] = useState<FilasData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filaAtiva, setFilaAtiva] = useState('falar_hoje');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('filas-admin', {
        body: { action: 'filas', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = resp as FilasData & { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setData(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar filas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const filaAtual = data?.filas?.[filaAtiva] ?? [];
  const infoAtual = FILA_INFO[filaAtiva];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
            <i className="ri-list-check-3 text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Inteligência / Filas</h2>
            <p className="text-sm text-gray-500">
              Quem merece sua atenção agora — e por quê. Respeita o override manual sempre.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap disabled:opacity-60"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Atualizar
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}

      {/* Navegação das filas */}
      <div className="bg-white rounded-2xl border border-gray-200 p-2 flex items-start gap-2 flex-wrap">
        {FILAS_DEFS.map((f) => {
          const count = data?.resumo?.[f.key] ?? 0;
          const ativa = filaAtiva === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilaAtiva(f.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                ativa ? 'bg-[#E8610A] text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className={f.icon}></i>
              {f.label}
              <span className={`text-[11px] font-bold px-1.5 rounded-full ${ativa ? 'bg-white/20' : 'bg-gray-100'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data ? (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${infoAtual.tone}`}>
              <i className={infoAtual.icon}></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{infoAtual.label}</h3>
              <p className="text-xs text-gray-500">{infoAtual.description}</p>
            </div>
          </div>

          {filaAtual.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 py-12 px-6 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-gray-50 text-gray-400 mb-3">
                <i className={`${infoAtual.icon} text-xl`}></i>
              </div>
              <p className="text-sm font-semibold text-gray-700">Nenhum registro nesta fila agora</p>
              <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
                {filaAtiva === 'nao_prospectar'
                  ? 'Decisões manuais de não prospecção aparecem aqui, com o motivo e o usuário que aplicou.'
                  : 'Os critérios desta fila serão preenchidos conforme os dados reais (contatos, mensagens e oportunidades) entrarem no sistema.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {filaAtual.map((item) => (
                <FilaCard key={item.key} item={item} password={password} onChanged={load} />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}